import React, { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import MarkdownMessage from '../../components/chat/MarkdownMessage';
import { wikiApi, type WikiPageView } from '../../services/wiki-api';
import { splitBlocks, changedBlockIndices, extractToc } from './markdownBlocks';

/**
 * Rendered document + TOC + review banner + 30-day change highlights (D45:
 * AI edits are live; the banner and highlights are the oversight surface).
 */
const ReadView: React.FC<{ page: WikiPageView; onReview: () => void }> = ({ page, onReview }) => {
  const queryClient = useQueryClient();
  const proseRef = useRef<HTMLDivElement>(null);

  // First-ever visit: silently establish the watermark so future edits
  // (not the entire back-catalog) show up as "since your last visit".
  const initWatermark = useMutation({
    mutationFn: () => wikiApi.markSeen(page.id, page.version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wikiPage', page.id] }),
  });
  useEffect(() => {
    if (page.lastSeenVersion === null && page.viewingVersion === null) {
      initWatermark.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id, page.lastSeenVersion]);

  const blocks = useMemo(() => splitBlocks(page.content), [page.content]);
  const highlighted = useMemo(
    () =>
      page.content30dAgo && page.viewingVersion === null
        ? changedBlockIndices(page.content30dAgo, page.content)
        : new Set<number>(),
    [page.content, page.content30dAgo, page.viewingVersion],
  );
  const toc = useMemo(() => extractToc(page.content), [page.content]);

  const scrollToHeading = (index: number): void => {
    const headings = proseRef.current?.querySelectorAll('h2, h3');
    headings?.[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      {page.viewingVersion !== null && (
        <div className="mx-5 mt-4 rounded-[9px] border border-warn/30 bg-warn/10 px-3.5 py-2.5 text-[12px] text-foreground">
          Viewing <b>v{page.viewingVersion}</b> — an older snapshot of this page. The live version is
          v{page.version}.
        </div>
      )}

      {page.unseen.count > 0 && page.viewingVersion === null && (
        <div className="flex items-center gap-2.5 mx-5 mt-4 rounded-[9px] border border-info/30 bg-info/10 px-3.5 py-2.5 text-[12px] text-foreground">
          <span>
            ✦ Zamino made <b>{page.unseen.count} {page.unseen.count === 1 ? 'change' : 'changes'}</b>{' '}
            since your last visit
            {page.unseen.summaries.length > 0 && (
              <> — {page.unseen.summaries.map((s) => s.replace(/\.\s*$/, '')).join('; ')}.</>
            )}
          </span>
          <button
            type="button"
            onClick={onReview}
            className="ml-auto font-bold text-info whitespace-nowrap hover:underline"
          >
            Review →
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_180px]">
        <div ref={proseRef} className="px-6 py-5">
          {blocks.map((block, i) => (
            <div
              key={i}
              className={clsx(
                highlighted.has(i) &&
                  'bg-success/10 border-l-[3px] border-success rounded-r-lg pl-3 pr-2 py-1 my-1',
              )}
            >
              <MarkdownMessage content={block} />
            </div>
          ))}
        </div>

        {toc.length > 0 && (
          <div className="hidden md:block border-l border-border px-4 py-5 text-[11.5px]">
            <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              On this page
            </div>
            {toc.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollToHeading(i)}
                className={clsx(
                  'block text-left py-1 text-muted-foreground hover:text-primary w-full truncate',
                  item.level === 3 && 'pl-3',
                )}
              >
                {item.text}
              </button>
            ))}
          </div>
        )}
      </div>

      {highlighted.size > 0 && (
        <div className="px-6 pb-4 text-[10.5px] text-muted-foreground">
          <span className="inline-block w-2.5 h-2.5 rounded-[3px] bg-success/20 align-[-1px] mr-1.5" />
          highlighted = changed in the last 30 days
        </div>
      )}
    </div>
  );
};

export default ReadView;
